import { useMemo } from "react";
import { fullWidth } from "../blocks/layout/columnSpans";
import { configToStageRows } from "../config/configToStageRows";
import { getStages, StageRow } from "../config/pageRows.types";
import { withVisibleItems } from "../config/visibilityRules";
import { DefaultFormId, DefaultViewName } from "../config/resolveView";
import { getFieldLabels, getFieldValidatorKeys } from "./entityPagePayload";
import { RecordDefinition } from "./recordDefinition.types";
import { ResolvedView } from "./EntityPageProvider.types";

/** What the page draws, and which views it offers, for the view the record was read for. */
export function useEntityPageLayout({
  definition,
  formId,
  resolvedView,
  record,
  tenantConfig,
  wizard,
}: {
  definition: RecordDefinition | undefined;
  formId: string;
  resolvedView: ResolvedView | null;
  record: Record<string, unknown> | null;
  /** The organisation's config, which some visibility rules read. */
  tenantConfig?: Record<string, unknown>;
  /** Whether the new-record wizard is still to be gone through, and the step it is on. */
  wizard?: { enabled: boolean; step: number };
}) {
  // A stored view renders from the layout that came back with the record; anything else
  // renders from code. `formId` is what the read was made with, so the two agree.
  const allRows = useMemo(() => {
    const config =
      resolvedView?.config && resolvedView.resolvedViewId === formId
        ? resolvedView.config
        : definition?.config;

    return config ? configToStageRows(config) : [];
  }, [definition, formId, resolvedView]);

  const visibleRows = useMemo(
    () => withVisibleItems(allRows, record ?? {}, tenantConfig ?? {}),
    [allRows, record, tenantConfig]
  );

  const wizardStages = useMemo(
    () =>
      getStages(visibleRows)
        .filter((stage) => stage.wizardStep)
        // Stable, so steps with no order keep the layout's.
        .sort((a, b) => (a.wizardOrder ?? 0) - (b.wizardOrder ?? 0)),
    [visibleRows]
  );
  const inWizard = Boolean(wizard?.enabled) && wizardStages.length > 0;
  const wizardStep = Math.min(wizard?.step ?? 0, wizardStages.length - 1);

  // Everything downstream - drawing, saving - sees what is shown. A wizard shows one step at
  // a time, so only the step is validated.
  const rows = visibleRows;
  const validationRows = useMemo<StageRow[]>(
    () =>
      inWizard
        ? [
            {
              columns: [
                {
                  span: fullWidth,
                  blocks: [{ kind: "stage", stage: wizardStages[wizardStep] }],
                },
              ],
            },
          ]
        : visibleRows,
    [inWizard, visibleRows, wizardStages, wizardStep]
  );

  // The stored views the service resolved, plus the shipped default, which has no row
  // to be listed from. It goes last, and is dropped where the organisation switched it
  // off or a stored view has taken its slug.
  const views = useMemo(() => {
    const stored = resolvedView?.views ?? [];
    const offerDefault =
      (resolvedView === null || resolvedView.defaultOffered) &&
      !stored.some((view) => view.key === DefaultFormId);

    return offerDefault
      ? [
          ...stored,
          {
            key: DefaultFormId,
            label: definition?.defaultViewName ?? DefaultViewName,
          },
        ]
      : stored;
  }, [definition, resolvedView]);

  const fieldLabels = useMemo(
    () => getFieldLabels(validationRows),
    [validationRows]
  );
  const fieldValidatorKeys = useMemo(
    () => getFieldValidatorKeys(validationRows),
    [validationRows]
  );

  return {
    rows,
    views,
    fieldLabels,
    fieldValidatorKeys,
    wizard: inWizard
      ? {
          step: wizardStep,
          stepCount: wizardStages.length,
          titles: wizardStages.map((stage) => stage.title),
          stage: wizardStages[wizardStep],
        }
      : null,
  };
}
